import React, { useEffect, useState } from 'react';

import { LinkRounded } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useParams } from 'react-router';

import ContentGroupsList from '../../../components/ContentGroupsList';
import ContentWrapper from '../../../components/ContentWrapper';
import CopyButton from '../../../components/CopyButton';
import DetailsModal, { Values } from '../../../components/DetailsModal';
import { NoPermissionMessage } from '../../../components/PageLayout';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useGetVideoCategoriesQuery, useNewVideoCategoryMutation,
} from '../../../services/videos';
import { Category } from '../../../services/videos/types';
import { PermissionType } from '../../../types/common';
import {
  SubjectDetails,
  SUBJECTS,
} from '../../../utils/common/constants';
import hasSubjectPermission from '../../../utils/common/hasSubjectPermission';
import useUserPermissions from '../../../utils/react/hooks/useUserPermissions';
import ShareButton from '../ShareButton';

const INITIAL_CATEGORY_FORM = {
  category: '',
  label: '',
  shortLabel: '',
};

const RootPage = () => {
  const { subject: querySubject } = useParams();
  const permissions = useUserPermissions();

  // null indicates the subject was fetched, but the user does not have permission
  const [subject, setSubject] = useState<SubjectDetails | undefined | null>(undefined);
  useEffect(() => {
    if (querySubject && permissions) {
      const subjectObj = SUBJECTS?.[querySubject];
      if (subjectObj && hasSubjectPermission(permissions, querySubject, PermissionType.Video)) {
        setSubject(subjectObj);
      } else {
        setSubject(null);
      }
    }
  }, [querySubject, permissions]);

  /**
   * Categories Queries & Mutations
   */
  const { data: categoriesData,
    isFetching: isCategoriesFetching } = useGetVideoCategoriesQuery({ subject: subject?.key || '' },
    { skip: !subject });
  const [newCategoryFormOpen, setNewCategoryFormOpen] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState(INITIAL_CATEGORY_FORM);
  const [newCategory,
    {
      isSuccess: isNewCategorySuccess,
      isLoading: isNewCategoryLoading,
      error: newCategoryError,
      reset: resetNewCategory,
    }] = useNewVideoCategoryMutation();

  /**
   * Categories functions
   */
  const resetModal = async () => {
    await resetNewCategory();
    setNewCategoryForm(INITIAL_CATEGORY_FORM);
  };

  const onCategoryEntryCreate = (values?: Values) => {
    newCategory({
      subject: subject?.key || '',
      attributes: values,
    });
  };

  // Close modal after category is created
  useEffect(() => {
    if (isNewCategorySuccess) {
      setNewCategoryFormOpen(false);
      resetModal();
    }
  }, [isNewCategorySuccess]);

  if (subject === undefined) {
    return (
      <SkeletonStack />
    );
  }

  if (subject === null) {
    return (
      <NoPermissionMessage />
    );
  }

  return (
    <>
      <ContentWrapper title="Actions" backButtonHref="/admin/subjects">
        <Stack direction="row" spacing={1}>
          <ShareButton
            targetTitle={subject?.label || 'Subject'}
            subject={subject?.key || ''}
          />
          <CopyButton
            size="medium"
            copyText={`${window.location.origin}/videos/${subject?.key || ''}`}
            startIcon={(<LinkRounded />)}
            buttonLabel="Copy Link"
          />
        </Stack>
      </ContentWrapper>
      <ContentWrapper title="Categories">
        <ContentGroupsList
          columns={[
            {
              field: 'category',
              headerName: 'Key',
              width: 120,
            },
            {
              field: 'label',
              headerName: 'Category',
              width: 360,
            },
          ]}
          data={[...(categoriesData?.data || [])].sort((a, b) => a.label.localeCompare(b.label))}
          loading={isCategoriesFetching}
          actions={{
            create: {
              fn: () => setNewCategoryFormOpen(true),
              label: 'Add Category',
            },
          }}
          getRowHref={(row: Category) => row?.category}
        />
      </ContentWrapper>
      <DetailsModal
        open={newCategoryFormOpen}
        handleClose={() => setNewCategoryFormOpen(false)}
        error={newCategoryError}
        title="Add New Category"
        fields={{
          category: {
            editable: true,
            type: 'text',
            label: '4-Char Key',
          },
          label: {
            editable: true,
            type: 'text',
          },
          shortLabel: {
            editable: true,
            type: 'text',
          },
        }}
        values={newCategoryForm}
        buttons={[{
          fn: onCategoryEntryCreate,
          label: 'Add',
          loading: isNewCategoryLoading,
        }]}
      />
    </>
  );
};

export default RootPage;
